import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Save, Copy, Plus, Plane, Loader2, Check, Share2, UserPlus, ExternalLink, Link2, Globe, CreditCard, PanelRightClose, PanelRightOpen, Wallet, Wand2, Image as ImageIcon, X as XIcon, CalendarRange, GripVertical } from "lucide-react";
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { getPublicTripUrl } from "@/lib/brand";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CotVersionSwitcher } from "./CotVersionSwitcher";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { CotDayBlock } from "@/pages/cotizador/CotDayBlock";
import { CotRoomConfig } from "@/pages/cotizador/CotRoomConfig";
import { CotAccommodations } from "@/pages/cotizador/CotAccommodations";
import { CotSearchProvider } from "@/pages/cotizador/CotCatalogSearch";
import { CotInfoDocuments } from "@/pages/cotizador/CotInfoDocuments";
import { CotCostSummary } from "@/pages/cotizador/CotCostSummary";
import { CotKimKimPricingSummary } from "@/pages/cotizador/CotKimKimPricingSummary";
import {
  COT_STATUS_LABELS, COT_STATUS_VARIANT, normalizeCotStatus, buildCalcItems, newDay, newService, newNote,
  newAccommodation, newRoom, ROOM_PAX_DEFAULT, daysBetween, dateAdd,
} from "@/pages/cotizador/shared";
import { CotShareModal } from "@/pages/cotizador/CotShareModal";
import { CotPaymentsModal } from "@/pages/cotizador/CotPaymentsModal";
import ImagePicker from "@/components/media/ImagePicker";

// It.6 E3 — Travel specialists reales de Espíritu Travel (foto + datos para el visor del cliente).
// Fuente: https://espiritutravel.com/travel-specialist/
const ET_SPECIALISTS = [
  { slug: "marina-courtaut", name: "Marina Courtaut", email: "marina@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/09/STAFF_014-768x816.jpg" },
  { slug: "beatriz-de-pablo", name: "Beatriz de Pablo", email: "beatriz@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/09/STAFF_015-768x816.jpg" },
  { slug: "rita-pagano", name: "Rita Pagano", email: "rita@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/09/STAFF_016-768x816.jpg" },
  { slug: "raquel-aceituno", name: "Raquel Aceituno", email: "raquel@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/10/STAFF_09-768x816.jpg" },
  { slug: "anita-matijasevic", name: "Anita Matijasevic", email: "anita@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/10/STAFF_06-768x816.jpg" },
  { slug: "hector-molina", name: "Héctor Molina", email: "hector@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/10/STAFF_04-768x816.jpg" },
  { slug: "giorgia-torre", name: "Giorgia Torre", email: "giorgia@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/10/STAFF_07-768x816.jpg" },
  { slug: "nadia-ramos", name: "Nadia Ramos", email: "nadia@espiritutravel.com", photo: "https://espiritutravel.com/wp-content/uploads/2025/10/STAFF_08-768x816.jpg" },
];

export default function CotizadorDetail({ cotId, onBack, onOpen }) {
  const qc = useQueryClient();
  const [doc, setDoc] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [liveVersion, setLiveVersion] = useState(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [confirmTrip, setConfirmTrip] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [priceDisplay, setPriceDisplay] = useState("total");
  const [publishBrand, setPublishBrand] = useState("et");
  const [publishing, setPublishing] = useState(false);
  const [shareWithOpen, setShareWithOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [costCollapsed, setCostCollapsed] = useState(() => localStorage.getItem("cot_cost_collapsed") === "1");
  const toggleCost = () => setCostCollapsed((c) => { localStorage.setItem("cot_cost_collapsed", c ? "0" : "1"); return !c; });
  const dragRef = useRef(null);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty | error
  const lastSavedRef = useRef(null);
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);

  useEffect(() => { setDoc(null); lastSavedRef.current = null; savingRef.current = false; dirtyRef.current = false; setSaveState("saved"); }, [cotId]);

  const cotQ = useQuery({
    queryKey: ["cotizacion", cotId],
    queryFn: async () => (await api.get(`/cotizaciones/${cotId}`)).data,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
  const partnersQ = useQuery({ queryKey: ["formula-partners"], queryFn: async () => (await api.get("/formulas/partners")).data });
  const citiesQ = useQuery({ queryKey: ["product-cities"], queryFn: async () => (await api.get("/products/cities")).data });
  const partners = partnersQ.data?.partners || [];
  const cityFacets = useMemo(() => (citiesQ.data?.items || []).map((c) => c.city), [citiesQ.data]);

  useEffect(() => {
    if (cotQ.data && (!doc || doc.id !== cotQ.data.id)) {
      setDoc(cotQ.data); setLiveResult(cotQ.data.result || null); setLiveVersion(cotQ.data.formula_version || null);
      lastSavedRef.current = JSON.stringify(buildPayload(cotQ.data)); setSaveState("saved");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotQ.data, cotId]);

  const items = useMemo(() => buildCalcItems(doc), [doc]);
  const calcKey = JSON.stringify({ p: doc?.partner, mk: doc?.markup_pct, cm: doc?.commission_pct, cmode: doc?.commission_mode, fx: doc?.fx_rate, pp: doc?.paypal_fee, items });

  useEffect(() => {
    if (!doc) return;
    if (!items.length) { setLiveResult(null); return; }
    setCalcBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/formulas/calc", {
          partner: doc.partner || "other",
          fx_rate: doc.fx_rate ? Number(doc.fx_rate) : null,
          paypal_fee: !!doc.paypal_fee,
          markup_pct_override: doc.markup_pct ?? null,
          commission_pct_override: doc.commission_pct ?? null,
          commission_mode_override: doc.commission_mode || null,
          items,
        });
        setLiveResult(data.result); setLiveVersion(data.formula_version);
      } catch (e) { /* preview only */ } finally { setCalcBusy(false); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcKey]);

  const lineById = useMemo(() => {
    const map = {};
    if (!liveResult?.lines) return map;
    let i = 0;
    (doc?.days || []).forEach((d) => (d.services || []).forEach((s) => { map[s.id] = liveResult.lines[i]; i += 1; }));
    return map;
  }, [liveResult, doc]);

  // Estancias por fecha con rol (réplica del «Creador de Itinerarios»):
  // primer día = check-in · días intermedios = estancia · último día = check-out.
  const staysByDate = useMemo(() => {
    const m = {};
    (doc?.accommodations || []).forEach((a) => {
      if (!a.name || !a.date_from || !a.date_to) return;
      const f = new Date(a.date_from), t = new Date(a.date_to);
      if (isNaN(f) || isNaN(t) || f >= t) return;
      for (let d = new Date(f); d <= t; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        const role = ds === a.date_from ? "checkin" : ds === a.date_to ? "checkout" : "stay";
        (m[ds] = m[ds] || []).push({ name: a.name, role, id: a.id });
      }
    });
    return m;
  }, [doc?.accommodations]);

  // Estancias cuyo check-in (date_from) cae en cada fecha → ficha plegable en ese día (sincronizada con el sumario).
  const checkinAccsByDate = useMemo(() => {
    const m = {};
    (doc?.accommodations || []).forEach((a) => {
      if (!a.date_from) return;
      const ds = String(a.date_from).slice(0, 10);
      (m[ds] = m[ds] || []).push(a);
    });
    return m;
  }, [doc?.accommodations]);

  const updateAccById = (id, updated) => set({ accommodations: (doc.accommodations || []).map((x) => (x.id === id ? updated : x)) });
  const removeAccById = (id) => set({ accommodations: (doc.accommodations || []).filter((x) => x.id !== id) });

  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const setDays = (fn) => setDoc((d) => ({ ...d, days: fn(d.days || []) }));

  const setDates = (key, v) => setDoc((d) => {
    const next = { ...d, [key]: v || null };
    const start = key === "start_date" ? v : d.start_date;
    const end = key === "end_date" ? v : d.end_date;
    const n = daysBetween(start, end);
    next.duration_days = n;
    if (n > 0) {
      let cur = [...(d.days || [])];
      if (n > cur.length) for (let i = cur.length; i < n; i++) cur.push(newDay(i + 1, dateAdd(start, i)));
      else if (n < cur.length) cur = cur.slice(0, n);
      next.days = cur.map((dd, i) => ({ ...dd, day_number: i + 1, date: start ? dateAdd(start, i) : dd.date }));
    }
    return next;
  });

  const updateDay = (dayId, patch) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, ...patch } : dy)));
  // Renumera y re-fecha los días en secuencia desde start_date; ajusta end_date/duration a la nueva longitud.
  const resequence = (days, start) => days.map((dy, i) => ({ ...dy, day_number: i + 1, date: start ? dateAdd(start, i) : dy.date }));
  const applyDays = (fn) => setDoc((d) => {
    const days = resequence(fn(d.days || []), d.start_date);
    const end = d.start_date && days.length ? dateAdd(d.start_date, days.length - 1) : d.end_date;
    return { ...d, days, end_date: end, duration_days: days.length };
  });
  const addDay = () => applyDays((days) => [...days, newDay(days.length + 1, dateAdd(doc.start_date, days.length))]);
  const insertDayAfter = (idx) => applyDays((days) => [...days.slice(0, idx + 1), newDay(idx + 2, null), ...days.slice(idx + 1)]);
  const removeDay = (dayId) => applyDays((days) => days.filter((dy) => dy.id !== dayId));
  const moveDay = (fromId, toId) => applyDays((days) => {
    const from = days.findIndex((x) => x.id === fromId), to = days.findIndex((x) => x.id === toId);
    return from < 0 || to < 0 ? days : arrayMove(days, from, to);
  });
  // Desplaza todas las fechas (inicio, fin, días y alojamientos) manteniendo la duración.
  const shiftDates = (newStart) => setDoc((d) => {
    if (!newStart || !d.start_date) return { ...d, start_date: newStart || d.start_date };
    const delta = Math.round((new Date(newStart) - new Date(d.start_date)) / 86400000);
    if (!delta) return d;
    const mv = (x) => (x ? dateAdd(x, delta) : x);
    return { ...d, start_date: newStart, end_date: mv(d.end_date),
      days: (d.days || []).map((dy) => ({ ...dy, date: mv(dy.date) })),
      accommodations: (d.accommodations || []).map((a) => ({ ...a, date_from: mv(a.date_from), date_to: mv(a.date_to) })) };
  });
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftTo, setShiftTo] = useState("");
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const addService = (dayId) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, services: [...(dy.services || []), newService({ city: dy.city || "", qty: doc.num_travelers || 1 })] } : dy)));
  const addHotelForDay = (day, hotel = {}) => {
    const from = hotel.date_from || day.date || doc.start_date || null;
    let to = hotel.date_to || (from ? dateAdd(from, 1) : (doc.end_date || null));
    if (from && to && new Date(to) <= new Date(from)) to = dateAdd(from, 1);
    const cfg = (doc.room_config && doc.room_config.length) ? doc.room_config : [{ room_type: "doble", pax: 2, quantity: 1 }];
    const totalQty = cfg.reduce((s, rc) => s + (rc.quantity || 1), 0) || 1;
    const rt = hotel.room_type || (cfg[0] && cfg[0].room_type) || "doble";
    const rpax = ROOM_PAX_DEFAULT[rt] || 2;
    const incl = Number(hotel.cost_tax_incl) || Number(hotel.cost_tax_excl) || 0;
    const excl = Number(hotel.cost_tax_excl) || incl;
    const rooms = Array.from({ length: totalQty }, () => newRoom({ room_type: rt, pax: rpax, cost_incl: incl, cost_excl: excl }));
    const acc = newAccommodation({ start_date: from, end_date: to }, rooms);
    acc.name = hotel.name || "";
    acc.product_id = hotel.id || null;
    acc.provider_id = hotel.provider_id || null;
    acc.city = hotel.city || day.city || "";
    acc.country = hotel.country || "";
    acc.currency = hotel.currency || "EUR";
    set({ accommodations: [...(doc.accommodations || []), acc] });
    toast.success("Estancia creada · Check-in / Check-out aplicados a los días");
  };
  const updateService = (dayId, sid, patch) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, services: (dy.services || []).map((s) => (s.id === sid ? { ...s, ...patch } : s)) } : dy)));
  const removeService = (dayId, sid) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, services: (dy.services || []).filter((s) => s.id !== sid) } : dy)));

  // It.6 E3 — Notas / sugerencias por día (estilo Travefy). No afectan al precio.
  const addNote = (dayId) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, notes: [...(dy.notes || []), newNote({})] } : dy)));
  const updateNote = (dayId, nid, patch) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, notes: (dy.notes || []).map((n) => (n.id === nid ? { ...n, ...patch } : n)) } : dy)));
  const removeNote = (dayId, nid) => setDays((days) => days.map((dy) => (dy.id === dayId ? { ...dy, notes: (dy.notes || []).filter((n) => n.id !== nid) } : dy)));

  const onDragStartService = (dayId, sid) => { dragRef.current = { dayId, sid }; };
  const onDropService = (targetDayId, targetIndex) => {
    const src = dragRef.current; dragRef.current = null;
    if (!src) return;
    setDays((days) => {
      let dragged = null;
      const stripped = days.map((dy) => {
        if (dy.id !== src.dayId) return dy;
        const services = [];
        (dy.services || []).forEach((s) => { if (s.id === src.sid) dragged = s; else services.push(s); });
        return { ...dy, services };
      });
      if (!dragged) return days;
      return stripped.map((dy) => {
        if (dy.id !== targetDayId) return dy;
        const services = [...(dy.services || [])];
        if (targetIndex < 0 || targetIndex >= services.length) services.push(dragged);
        else services.splice(targetIndex, 0, dragged);
        return { ...dy, services };
      });
    });
  };

  const buildPayload = (d) => ({
    name: d.name, itinerary_title: d.itinerary_title || "", main_traveler: d.name,
    client_email: d.client_email || "",
    destination: d.destination || "", cover_image: d.cover_image || "",
    start_date: d.start_date || null, end_date: d.end_date || null, duration_days: d.duration_days || 0,
    num_travelers: parseInt(d.num_travelers || 1, 10), adults: parseInt(d.num_travelers || 1, 10),
    number_of_children: parseInt(d.number_of_children || 0, 10), children_ages: d.children_ages || "",
    partner: d.partner || "other", markup_pct: d.markup_pct ?? null, commission_pct: d.commission_pct ?? null,
    commission_mode: d.commission_mode || "none", fx_rate: d.fx_rate ? Number(d.fx_rate) : null, paypal_fee: !!d.paypal_fee,
    status: d.status || "borrador", notes: d.notes || "",
    room_config: d.room_config || [], days: d.days || [], accommodations: d.accommodations || [],
    info_documents: d.info_documents || [], show_info_documents: d.show_info_documents !== false,
    whitelabel_enabled: !!d.whitelabel_enabled,
    consultant: d.consultant || null,
  });
  const payload = () => buildPayload(doc);

  // Autoguardado con debounce (estilo «Creador de Itinerarios»): guarda solo si hay cambios.
  const doSave = async () => {
    if (!doc || doc.id !== cotId) return;
    const snap = JSON.stringify(buildPayload(doc));
    if (snap === lastSavedRef.current) { setSaveState("saved"); return; }
    if (savingRef.current) { dirtyRef.current = true; return; }
    savingRef.current = true; setSaveState("saving");
    try {
      const { data } = await api.put(`/cotizaciones/${cotId}`, buildPayload(doc));
      lastSavedRef.current = JSON.stringify(buildPayload(doc));
      setLiveVersion(data.formula_version || null);
      qc.setQueryData(["cotizacion", cotId], data);
      qc.invalidateQueries({ queryKey: ["cotizaciones"] });
      setSaveState("saved");
    } catch (e) {
      setSaveState("error"); toast.error(apiErrorMessage(e));
    } finally {
      savingRef.current = false;
      if (dirtyRef.current) { dirtyRef.current = false; doSave(); }
    }
  };

  const payloadStr = useMemo(() => (doc ? JSON.stringify(buildPayload(doc)) : ""), [doc]);
  useEffect(() => {
    if (!doc || doc.id !== cotId || lastSavedRef.current === null) return;
    if (payloadStr === lastSavedRef.current) { setSaveState("saved"); return; }
    setSaveState("dirty");
    const t = setTimeout(() => { doSave(); }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadStr]);

  // Guard: nunca persistir si el doc local no corresponde a la cotización abierta.
  const ensureSynced = () => {
    if (!doc || doc.id !== cotId) throw new Error("Cotización no sincronizada, reábrela e inténtalo de nuevo");
  };

  const dupMut = useMutation({
    mutationFn: () => { ensureSynced(); return api.put(`/cotizaciones/${cotId}`, payload()).then((r) => { lastSavedRef.current = JSON.stringify(buildPayload(doc)); setSaveState("saved"); return r; }).then(() => api.post(`/cotizaciones/${cotId}/duplicate`)); },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["cotizaciones"] }); toast.success(`Versión v${res.data.version} creada`); onOpen?.(res.data.id); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const tripMut = useMutation({
    mutationFn: () => { ensureSynced(); return api.put(`/cotizaciones/${cotId}`, payload()).then(() => api.post(`/cotizaciones/${cotId}/to-trip`)); },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["cotizaciones"] }); setConfirmTrip(false); set({ status: "vendido", trip_id: res.data.trip_id }); toast.success(`Trip creado con ${res.data.reservas} reservas`); },
    onError: (e) => { setConfirmTrip(false); toast.error(apiErrorMessage(e)); },
  });

  const SAVE_LABEL = { saving: "Guardando…", saved: "Guardado", dirty: "Cambios sin guardar", error: "Error al guardar" };

  const publicUrl = doc?.public_token ? getPublicTripUrl(doc.brand, doc.public_token, "trip") : "";
  const payUrl = doc?.public_token ? getPublicTripUrl(doc.brand, doc.public_token, "pay") : "";
  const openPublish = () => {
    setPriceDisplay(doc?.price_display || "total");
    setPublishBrand(doc?.brand || "et");
    setPublishOpen(true);
  };
  const doPublish = async () => {
    setPublishing(true);
    try {
      ensureSynced();
      await api.put(`/cotizaciones/${cotId}`, payload());
      lastSavedRef.current = JSON.stringify(buildPayload(doc)); setSaveState("saved");
      const { data } = await api.post(`/cotizaciones/${cotId}/publish`, { price_display: priceDisplay, brand: publishBrand });
      set({ public_token: data.token, published: true, price_display: data.price_display, brand: data.brand });
      qc.invalidateQueries({ queryKey: ["cotizaciones"] });
      toast.success(`Trip Plan publicado (${data.brand === "vdv" ? "Viaja de Verdad" : "Espíritu Travel"})`);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setPublishing(false); }
  };
  const doUnpublish = async () => {
    setPublishing(true);
    try {
      await api.post(`/cotizaciones/${cotId}/unpublish`);
      set({ published: false });
      qc.invalidateQueries({ queryKey: ["cotizaciones"] });
      toast.success("Trip Plan despublicado");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setPublishing(false); }
  };
  // Re-enriquecer contenido: resetea descripciones/fotos y re-ejecuta matcher TP + KimKim.
  // Útil para cotizaciones importadas antes del fix del matcher (Feb 2026).
  const [resetEnrichOpen, setResetEnrichOpen] = useState(false);
  const [resettingEnrich, setResettingEnrich] = useState(false);
  const doResetEnrichment = async () => {
    setResettingEnrich(true);
    try {
      ensureSynced();
      await api.put(`/cotizaciones/${cotId}`, payload());
      lastSavedRef.current = JSON.stringify(buildPayload(doc)); setSaveState("saved");
      const { data } = await api.post(`/cotizaciones/${cotId}/reset-enrichment`);
      const parts = [
        data.reset_services != null && `${data.reset_services} servicios reseteados`,
        data.splits_applied ? `${data.splits_applied} bundle(s) divididos` : null,
        data.duplicates_removed ? `${data.duplicates_removed} duplicados eliminados` : null,
        data.republished ? "snapshot regenerado" : null,
      ].filter(Boolean);
      toast.success(`Contenido re-enriquecido · ${parts.join(" · ")}`);
      qc.invalidateQueries({ queryKey: ["cotizacion", cotId] });
      setResetEnrichOpen(false);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setResettingEnrich(false); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(publicUrl); toast.success("Enlace copiado"); };
  const copyPayLink = () => { navigator.clipboard?.writeText(payUrl); toast.success("Enlace de pago copiado"); };

  if (cotQ.isLoading || !doc) return <div className="space-y-3"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <CotSearchProvider destination={doc.destination || ""}>
    <div className="space-y-4" data-testid="cotizador-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="cot-back"><ChevronLeft className="mr-1 h-4 w-4" />Cotizaciones</Button>
          <h1 className="font-heading text-lg font-bold tracking-tight sm:text-xl">{doc.name || "Cotización"}</h1>
          <Badge variant={COT_STATUS_VARIANT[normalizeCotStatus(doc.status)] || "secondary"}>{COT_STATUS_LABELS[normalizeCotStatus(doc.status)] || "Borrador"}</Badge>
          <CotVersionSwitcher cotId={cotId} onOpen={onOpen} />
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className={`flex items-center gap-1.5 text-xs ${saveState === "error" ? "text-destructive" : saveState === "dirty" ? "text-amber-600" : "text-muted-foreground"}`} data-testid="cot-save-state">
            {saveState === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saveState === "saved" && <Check className="h-3.5 w-3.5 text-emerald-600" />}
            {SAVE_LABEL[saveState]}
          </span>
          <Button variant="outline" size="sm" onClick={() => setShareWithOpen(true)} data-testid="cot-share-with"><UserPlus className="mr-1.5 h-4 w-4" />Compartir con{(doc.shared_with?.length) ? ` (${doc.shared_with.length})` : ""}</Button>
          {doc.published && (
            <Button variant="outline" size="sm" onClick={() => setPaymentsOpen(true)} data-testid="cot-payments-btn" className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
              <Wallet className="mr-1.5 h-4 w-4" />Cobros
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => dupMut.mutate()} disabled={dupMut.isPending} data-testid="cot-duplicate" title="Crea una nueva versión (v2, v3…) de esta cotización"><Copy className="mr-1.5 h-4 w-4" />Nueva versión</Button>
          <Button variant="outline" size="sm" onClick={() => setResetEnrichOpen(true)} disabled={resettingEnrich} data-testid="cot-reset-enrich" title="Restablece descripciones y fotos re-ejecutando el matcher de plantillas TP y el catálogo KimKim">
            {resettingEnrich ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}Re-enriquecer
          </Button>
          <Button variant={doc.published ? "default" : "outline"} size="sm" onClick={openPublish} data-testid="cot-share" className={doc.published ? "bg-emerald-600 hover:bg-emerald-700" : ""}><Share2 className="mr-1.5 h-4 w-4" />{doc.published ? "Publicado" : "Compartir"}</Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmTrip(true)} disabled={!!doc.trip_id} data-testid="cot-to-trip"><Plane className="mr-1.5 h-4 w-4" />{doc.trip_id ? "Convertida" : "Convertir a Trip"}</Button>
          <Button size="sm" onClick={() => doSave()} disabled={saveState === "saving"} data-testid="cot-save">{saveState === "saving" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Guardar</Button>
        </div>
      </div>

      <div className={`grid gap-4 ${costCollapsed ? "lg:grid-cols-1" : "lg:grid-cols-[1fr_340px]"}`}>
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Datos de la cotización</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Título del itinerario a ancho completo, como en el Creador legacy */}
              <div className="space-y-1.5">
                <Label>Título del itinerario *</Label>
                <Input value={doc.itinerary_title || ""} onChange={(e) => set({ itinerary_title: e.target.value })} placeholder="Ej.: A Portuguese Getaway: Best of Lisbon and Porto" data-testid="cot-itinerary-title" />
                <p className="text-[11px] text-muted-foreground">Nombre visible en el itinerario del cliente. El nombre del viajero aparecerá debajo.</p>
              </div>

              {/* Banda de metadata superior — réplica del Creador de Itinerarios legacy.
                  Fila 1: Viajero · Fuente · Inicio · Fin · Noches
                  Fila 2: Adultos · Niños · Edades niños · Pax (total) */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="cot-meta-strip-1">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Viajero principal</Label>
                  <Input value={doc.name || ""} onChange={(e) => set({ name: e.target.value, main_traveler: e.target.value })} data-testid="cot-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fuente</Label>
                  <Select value={doc.partner || "other"} onValueChange={(v) => { const r = partners.find((p) => p.key === v); set({ partner: v, commission_mode: r?.commission_mode || "none" }); }}>
                    <SelectTrigger data-testid="cot-partner"><SelectValue /></SelectTrigger>
                    <SelectContent>{partners.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Inicio</Label>
                  <Input type="date" value={doc.start_date || ""} onChange={(e) => setDates("start_date", e.target.value)} data-testid="cot-start" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Fin</Label>
                  <Input type="date" value={doc.end_date || ""} onChange={(e) => setDates("end_date", e.target.value)} data-testid="cot-end" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Noches</Label>
                  {(() => {
                    const d0 = doc.start_date ? new Date(`${doc.start_date}T00:00:00`) : null;
                    const d1 = doc.end_date ? new Date(`${doc.end_date}T00:00:00`) : null;
                    let nights = 0;
                    if (d0 && d1 && !isNaN(d0) && !isNaN(d1) && d1 >= d0) {
                      nights = Math.round((d1 - d0) / 86400000);
                    }
                    return (
                      <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm tabular-nums" data-testid="cot-days-nights">
                        <span className="font-semibold">{nights}</span>
                        <span className="ml-1.5 text-muted-foreground">{nights === 1 ? "noche" : "noches"}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="cot-meta-strip-2">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Adultos</Label>
                  <Input type="number" min="1" value={doc.num_travelers ?? 1} onChange={(e) => set({ num_travelers: parseInt(e.target.value || "1", 10) })} data-testid="cot-pax" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Niños</Label>
                  <Input type="number" min="0" value={doc.number_of_children ?? 0} onChange={(e) => set({ number_of_children: parseInt(e.target.value || "0", 10) })} data-testid="cot-children" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Edades niños <span className="text-muted-foreground/70 normal-case">(sin niños)</span></Label>
                  <Input value={doc.children_ages || ""} onChange={(e) => set({ children_ages: e.target.value })} placeholder="—" data-testid="cot-children-ages" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Pax (total)</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm tabular-nums" data-testid="cot-pax-total">
                    <span className="font-semibold">{(parseInt(doc.num_travelers || 0, 10) || 0) + (parseInt(doc.number_of_children || 0, 10) || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Notas internas — a ancho completo, como en el legacy */}
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notas internas</Label>
                <Textarea rows={2} value={doc.notes || ""} onChange={(e) => set({ notes: e.target.value })} placeholder="Recordatorios, preferencias del cliente, condiciones especiales, contexto para otros agentes…" data-testid="cot-notes" />
              </div>

              {/* Extras SOFI (no están en el legacy): destino, estado, portada */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Destino</Label>
                  <Input value={doc.destination || ""} onChange={(e) => set({ destination: e.target.value })} data-testid="cot-destination" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Estado</Label>
                  <Select value={normalizeCotStatus(doc.status)} onValueChange={(v) => set({ status: v })}>
                    <SelectTrigger data-testid="cot-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(COT_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Foto de portada del itinerario</Label>
                <CoverImagePicker value={doc.cover_image || ""} destination={doc.destination || ""} onChange={(u) => set({ cover_image: u })} />
                <p className="text-[11px] text-muted-foreground">Si dejas en blanco, se usará la primera imagen de una actividad. Elige una foto de Shutterstock para evitar pixelación.</p>
              </div>
            </CardContent>
          </Card>

          <CotRoomConfig config={doc.room_config || []} numTravelers={doc.num_travelers} onChange={(rc) => set({ room_config: rc })} />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Itinerario día a día</h2>
            <div className="flex items-center gap-1.5">
              {shiftOpen ? (
                <div className="flex items-center gap-1.5 rounded-md border bg-card px-2 py-1" data-testid="cot-shift-dates">
                  <span className="text-xs text-muted-foreground">Nuevo inicio</span>
                  <Input type="date" value={shiftTo} onChange={(e) => setShiftTo(e.target.value)} className="h-7 w-36 text-xs" data-testid="cot-shift-dates-input" />
                  <Button size="sm" className="h-7 text-xs" disabled={!shiftTo} onClick={() => { shiftDates(shiftTo); setShiftOpen(false); setShiftTo(""); }} data-testid="cot-shift-dates-apply">Desplazar</Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShiftOpen(false)}><XIcon className="h-3.5 w-3.5" /></Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => { setShiftTo(doc.start_date || ""); setShiftOpen(true); }} disabled={!doc.start_date} title="Mueve todas las fechas (días y hoteles) manteniendo la duración" data-testid="cot-shift-dates-btn"><CalendarRange className="mr-1.5 h-4 w-4" />Desplazar fechas</Button>
              )}
              <Button size="sm" onClick={addDay} data-testid="cot-add-day"><Plus className="mr-1.5 h-4 w-4" />Añadir día</Button>
            </div>
          </div>

          {(doc.days || []).length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Sin días. Pon fechas de inicio/fin (se generan solos) o pulsa «Añadir día».</CardContent></Card>
          ) : (
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => { if (over && active.id !== over.id) moveDay(active.id, over.id); }}>
            <SortableContext items={doc.days.map((d) => d.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {doc.days.map((d, idx) => (
                <SortableDay key={d.id} id={d.id}>{(handle) => (
                <CotDayBlock
                  day={d} idx={idx} numTravelers={doc.num_travelers} cityFacets={cityFacets} dragHandle={handle} onInsertAfter={() => insertDayAfter(idx)}
                  lineById={lineById} stays={d.date ? (staysByDate[String(d.date).slice(0, 10)] || []) : []}
                  roomConfig={doc.room_config || []} markup={doc.markup_pct}
                  checkinAccs={d.date ? (checkinAccsByDate[String(d.date).slice(0, 10)] || []) : []}
                  onUpdateAccommodation={updateAccById} onRemoveAccommodation={removeAccById}
                  onUpdateDay={(patch) => updateDay(d.id, patch)} onAddHotel={(hotel) => addHotelForDay(d, hotel)} onAddService={() => addService(d.id)}
                  onUpdateService={(sid, patch) => updateService(d.id, sid, patch)} onRemoveService={(sid) => removeService(d.id, sid)}
                  onAddNote={() => addNote(d.id)} onUpdateNote={(nid, patch) => updateNote(d.id, nid, patch)} onRemoveNote={(nid) => removeNote(d.id, nid)}
                  onRemoveDay={() => removeDay(d.id)} onDragStartService={onDragStartService} onDropService={onDropService}
                />
                )}</SortableDay>
              ))}
            </div>
            </SortableContext>
            </DndContext>
          )}

          <CotAccommodations
            accommodations={doc.accommodations || []} roomConfig={doc.room_config || []} numTravelers={doc.num_travelers}
            markup={doc.markup_pct} startDate={doc.start_date} endDate={doc.end_date}
            onChange={(accs) => set({ accommodations: accs })}
          />

          <CotInfoDocuments
            sections={doc.info_documents || []} show={doc.show_info_documents !== false}
            onChange={(secs) => set({ info_documents: secs })}
            onToggle={(v) => set({ show_info_documents: v })}
          />
        </div>

        {costCollapsed ? (
          <button
            onClick={toggleCost}
            data-testid="cot-cost-expand"
            title="Mostrar coste"
            className="fixed right-0 top-1/2 z-40 flex -translate-y-1/2 items-center gap-1.5 rounded-l-lg border border-r-0 border-border bg-primary px-2 py-3 text-primary-foreground shadow-md transition-transform hover:px-3"
          >
            <PanelRightOpen className="h-4 w-4" />
            <span className="text-[11px] font-semibold [writing-mode:vertical-rl]">Coste</span>
          </button>
        ) : (
          <div className="space-y-2 lg:sticky lg:top-4 lg:self-start">
            <div className="flex justify-end">
              <button
                onClick={toggleCost}
                data-testid="cot-cost-collapse"
                title="Colapsar panel de coste"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PanelRightClose className="h-3.5 w-3.5" /> Colapsar
              </button>
            </div>
            {doc?.pricing_locked && doc?.import_source === "kimkim"
              ? <CotKimKimPricingSummary doc={doc} />
              : <CotCostSummary result={liveResult} version={liveVersion} doc={doc} partners={partners} onSet={set} />}
            {calcBusy && !doc?.pricing_locked && <p className="text-center text-[11px] text-muted-foreground">Recalculando…</p>}
          </div>
        )}
      </div>

      <AlertDialog open={confirmTrip} onOpenChange={setConfirmTrip}>
        <AlertDialogContent data-testid="cot-trip-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Convertir en Trip?</AlertDialogTitle>
            <AlertDialogDescription>Se guardará la cotización y se creará un viaje con sus servicios y alojamientos como reservas. Quedará marcada como «Vendida».</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => tripMut.mutate()} disabled={tripMut.isPending} data-testid="cot-trip-confirm">{tripMut.isPending ? "Creando…" : "Convertir"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetEnrichOpen} onOpenChange={setResetEnrichOpen}>
        <AlertDialogContent data-testid="cot-reset-enrich-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Re-enriquecer contenido?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrarán las descripciones de venta y las fotos de todos los servicios y alojamientos, y se volverán a generar automáticamente desde las plantillas TP y el catálogo KimKim. Las ediciones manuales a esos campos se perderán. Si el trip está publicado, el snapshot se regenerará al vuelo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resettingEnrich}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doResetEnrichment} disabled={resettingEnrich} data-testid="cot-reset-enrich-confirm">
              {resettingEnrich ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Procesando…</> : "Re-enriquecer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-md" data-testid="cot-publish-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" />Compartir Trip Plan</DialogTitle></DialogHeader>
          <p className="-mt-1 text-sm text-muted-foreground">Genera un enlace público (sin login) con el itinerario para el cliente.</p>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Marca del itinerario</Label>
              <Select value={publishBrand} onValueChange={setPublishBrand}>
                <SelectTrigger data-testid="cot-publish-brand"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="et" data-testid="cot-publish-brand-et">Espíritu Travel</SelectItem>
                  <SelectItem value="vdv" data-testid="cot-publish-brand-vdv">Viaja de Verdad</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Cambia el logo y el contacto (email/web) que ve el cliente en el visor y el PDF.</p>
            </div>
            <div className="space-y-1.5">
              <Label>¿Qué precio mostrar al cliente?</Label>
              <Select value={priceDisplay} onValueChange={setPriceDisplay}>
                <SelectTrigger data-testid="cot-publish-price"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="total">PVP final total (sin desglose)</SelectItem>
                  <SelectItem value="total_pp">Precio por persona</SelectItem>
                  <SelectItem value="hidden">Sin precio (solo itinerario)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Travel Specialist (foto en el visor)</Label>
              <Select
                value={doc.consultant?.slug || doc.consultant?.name || "none"}
                onValueChange={(v) => {
                  if (v === "none") return set({ consultant: null });
                  const sp = ET_SPECIALISTS.find((s) => s.slug === v);
                  set({ consultant: sp ? { slug: sp.slug, name: sp.name, role: "Travel Specialist", photo: sp.photo, email: sp.email, phone: "" } : null });
                }}>
                <SelectTrigger data-testid="cot-publish-specialist"><SelectValue placeholder="Selecciona un especialista" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especialista</SelectItem>
                  {ET_SPECIALISTS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">El cliente verá la foto y el contacto del especialista en la propuesta.</p>
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div className="space-y-0.5">
                <Label className="text-sm">Information &amp; Documents</Label>
                <p className="text-[11px] text-muted-foreground">Términos, qué incluye, política de cancelación… al final del itinerario.</p>
              </div>
              <Switch checked={doc.show_info_documents !== false} onCheckedChange={(v) => set({ show_info_documents: v })} data-testid="cot-publish-info-toggle" />
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div className="space-y-0.5">
                <Label className="text-sm">Marca blanca (travel agents)</Label>
                <p className="text-[11px] text-muted-foreground">Permite a un tercero poner su logo y contacto. Desactivado por defecto.</p>
              </div>
              <Switch checked={!!doc.whitelabel_enabled} onCheckedChange={(v) => set({ whitelabel_enabled: v })} data-testid="cot-publish-whitelabel-toggle" />
            </div>
            {doc.published && publicUrl && (
              <div className="space-y-1.5">
                <Label>Enlace público</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={publicUrl} className="text-xs" data-testid="cot-publish-link" onFocus={(e) => e.target.select()} />
                  <Button type="button" size="icon" variant="outline" onClick={copyLink} title="Copiar" data-testid="cot-publish-copy"><Link2 className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="outline" onClick={() => window.open(publicUrl, "_blank")} title="Abrir" data-testid="cot-publish-open"><ExternalLink className="h-4 w-4" /></Button>
                </div>
                <p className="flex items-center gap-1 text-[11px] text-emerald-600"><Globe className="h-3 w-3" /> Publicado · los cambios se reflejan al volver a publicar.</p>
              </div>
            )}
            {doc.published && payUrl && doc.price_display !== "hidden" && doc.brand !== "vdv" && (
              <div className="space-y-1.5 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="cot-paylink-block">
                <Label className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-primary" />Enlace de pago (sin itinerario)</Label>
                <p className="text-[11px] text-muted-foreground">Página de cobro directo con 4 opciones (señal 30%, total, saldo o importe parcial) y datos del pasajero.</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={payUrl} className="text-xs" data-testid="cot-paylink-input" onFocus={(e) => e.target.select()} />
                  <Button type="button" size="icon" variant="outline" onClick={copyPayLink} title="Copiar" data-testid="cot-paylink-copy"><Link2 className="h-4 w-4" /></Button>
                  <Button type="button" size="icon" variant="outline" onClick={() => window.open(payUrl, "_blank")} title="Abrir" data-testid="cot-paylink-open"><ExternalLink className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {doc.published && <Button variant="outline" onClick={doUnpublish} disabled={publishing} data-testid="cot-unpublish">Despublicar</Button>}
            <Button onClick={doPublish} disabled={publishing} data-testid="cot-publish-confirm">{publishing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Share2 className="mr-1.5 h-4 w-4" />}{doc.published ? "Actualizar y republicar" : "Publicar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CotShareModal
        open={shareWithOpen}
        onOpenChange={setShareWithOpen}
        cotId={cotId}
        ownerEmail={doc.created_by}
        sharedWith={doc.shared_with || []}
        onChange={(list) => set({ shared_with: list })}
      />

      {paymentsOpen && doc?.public_token && (
        <CotPaymentsModal
          cotId={cotId}
          publicToken={doc.public_token}
          clientEmail={doc.client_email || doc.contact_email || ""}
          onClose={() => setPaymentsOpen(false)}
        />
      )}
    </div>
    </CotSearchProvider>
  );
}


/** Selector de foto de portada del itinerario (usa ImagePicker con Shutterstock).
 *  Muestra una miniatura si hay foto; abre el picker al pulsar "Cambiar" o "Elegir foto".
 */
function CoverImagePicker({ value, destination, onChange }) {
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [value]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const src = value ? (String(value).startsWith("/api/") ? `${BACKEND}${value}` : value) : "";
  return (
    <div className="flex items-start gap-3" data-testid="cot-cover-picker">
      {src ? (
        <div className="relative h-28 w-48 shrink-0 overflow-hidden rounded-md border bg-muted">
          {imgError ? (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">Sin vista previa</div>
          ) : (
            <img src={src} alt="Portada del itinerario" className="h-full w-full object-cover" onError={() => setImgError(true)} />
          )}
        </div>
      ) : (
        <div className="flex h-28 w-48 shrink-0 items-center justify-center rounded-md border border-dashed bg-muted/40 text-xs text-muted-foreground">
          <ImageIcon className="mr-1 h-4 w-4" />Sin foto
        </div>
      )}
      <div className="flex flex-col gap-1.5 pt-1">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="cot-cover-change">
          <ImageIcon className="mr-1.5 h-3.5 w-3.5" />{src ? "Cambiar portada" : "Elegir portada"}
        </Button>
        {src && (
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onChange("")} data-testid="cot-cover-remove">
            <XIcon className="mr-1.5 h-3.5 w-3.5" />Quitar
          </Button>
        )}
      </div>
      <ImagePicker
        open={open}
        onOpenChange={setOpen}
        title="Foto de portada del itinerario"
        orientation="horizontal"
        defaultQuery={destination || ""}
        onSelect={(picked) => {
          const url = picked?.url || picked?.preview_url || (typeof picked === "string" ? picked : "");
          if (url) { onChange(url); setOpen(false); }
        }}
      />
    </div>
  );
}


function SortableDay({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const handle = (
    <button type="button" {...attributes} {...listeners} className="flex h-7 w-5 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted active:cursor-grabbing" title="Arrastra para reordenar el día" aria-label="Reordenar día" data-testid={`cot-day-drag-${id}`}>
      <GripVertical className="h-4 w-4" />
    </button>
  );
  return <div ref={setNodeRef} style={style}>{children(handle)}</div>;
}
